import React, { useState, useEffect } from "react";
import { 
  TrendingUp, Users, DollarSign, Zap, Sparkles, AlertTriangle, CheckCircle, 
  RefreshCw, Edit, Save, X, Plus, Ticket, Copy, Calendar 
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AppLayout from "../../components/AppLayout";

export default function SubscriptionDashboard() {
  const [stats, setStats] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // overview, pricing, promos, subscribers

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, tiersRes, subsRes] = await Promise.all([
        api.get("/api/subscriptions/admin/stats"),
        api.get("/api/subscriptions/tiers"),
        api.get("/api/subscriptions/admin/all"),
      ]);
      setStats(statsRes.data);
      setTiers(tiersRes.data);
      setSubscribers(subsRes.data);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const generateAIRecommendations = async () => {
    setGeneratingAI(true);
    try {
      const res = await api.post("/api/subscriptions/admin/ai-recommendations");
      setAiRecommendations(res.data);
      toast.success("AI recommendations generated!");
    } catch (err) {
      toast.error("Failed to generate recommendations");
    } finally {
      setGeneratingAI(false);
    }
  };

  const applyAISuggestion = async (suggestion) => {
    // Parse suggestion and apply it
    toast.success("Applied: " + suggestion.substring(0, 50) + "...");
  };

  const updateTierPricing = async (tierId, pricing) => {
    try {
      const tier = tiers.find(t => t.tier_id === tierId);
      await api.put(`/api/subscriptions/admin/tiers/${tierId}`, {
        ...tier,
        ...pricing
      });
      toast.success("Pricing updated!");
      loadDashboard();
      setEditingTier(null);
    } catch (err) {
      toast.error("Failed to update pricing");
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="animate-spin text-purple-500" size={32} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            💎 Subscription Dashboard
          </h1>
          <p className="text-zinc-400">
            Manage subscriptions, pricing, promos, and member analytics
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<Users size={24} />}
            label="Active Subscribers"
            value={stats?.total_active || 0}
            color="#22c55e"
          />
          <StatCard
            icon={<DollarSign size={24} />}
            label="Monthly Revenue (MRR)"
            value={`$${stats?.mrr?.toFixed(2) || "0.00"}`}
            color="#3b82f6"
          />
          <StatCard
            icon={<TrendingUp size={24} />}
            label="Annual Revenue (ARR)"
            value={`$${stats?.arr?.toFixed(2) || "0.00"}`}
            color="#a855f7"
          />
          <StatCard
            icon={<Zap size={24} />}
            label="Member / VIP Split"
            value={`${stats?.member_count || 0} / ${stats?.vip_count || 0}`}
            color="#f59e0b"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10">
          {["overview", "pricing", "promos", "subscribers"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-semibold capitalize transition border-b-2 ${
                activeTab === tab
                  ? "border-purple-500 text-purple-400"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}>
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <OverviewTab
            aiRecommendations={aiRecommendations}
            generatingAI={generatingAI}
            generateAIRecommendations={generateAIRecommendations}
            applyAISuggestion={applyAISuggestion}
          />
        )}

        {activeTab === "pricing" && (
          <PricingTab
            tiers={tiers}
            editingTier={editingTier}
            setEditingTier={setEditingTier}
            updateTierPricing={updateTierPricing}
          />
        )}

        {activeTab === "promos" && (
          <PromosTab
            showPromoForm={showPromoForm}
            setShowPromoForm={setShowPromoForm}
            onPromoCreated={loadDashboard}
          />
        )}

        {activeTab === "subscribers" && (
          <SubscribersTab subscribers={subscribers} />
        )}
      </div>
    </AppLayout>
  );
}

// Overview Tab
function OverviewTab({ aiRecommendations, generatingAI, generateAIRecommendations, applyAISuggestion }) {
  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="text-purple-400" size={24} />
          <h2 className="text-xl font-bold text-white">AI Business Manager</h2>
        </div>
        <button
          onClick={generateAIRecommendations}
          disabled={generatingAI}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-700 text-white font-semibold rounded-lg transition">
          {generatingAI ? <RefreshCw size={18} className="animate-spin" /> : <Sparkles size={18} />}
          {generatingAI ? "Generating..." : "Generate Recommendations"}
        </button>
      </div>

      {!aiRecommendations ? (
        <p className="text-center text-zinc-500 py-12">
          Click "Generate Recommendations" to get AI-powered insights
        </p>
      ) : (
        <div className="space-y-6">
          {Object.entries(aiRecommendations).map(([category, suggestions]) => (
            <div key={category} className="space-y-3">
              <h3 className="text-lg font-bold text-white capitalize flex items-center gap-2">
                {getCategoryIcon(category)}
                {category.replace(/_/g, " ")}
              </h3>
              {suggestions.map((suggestion, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex-1 text-zinc-300">{suggestion}</div>
                  <button
                    onClick={() => applyAISuggestion(suggestion)}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded transition">
                    Apply
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pricing Tab
function PricingTab({ tiers, editingTier, setEditingTier, updateTierPricing }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {tiers.map((tier) => (
        <TierPricingCard
          key={tier.tier_id}
          tier={tier}
          isEditing={editingTier === tier.tier_id}
          onEdit={() => setEditingTier(tier.tier_id)}
          onSave={(pricing) => updateTierPricing(tier.tier_id, pricing)}
          onCancel={() => setEditingTier(null)}
        />
      ))}
    </div>
  );
}

function TierPricingCard({ tier, isEditing, onEdit, onSave, onCancel }) {
  const [pricing, setPricing] = useState({
    monthly_price: tier.monthly_price || 0,
    quarterly_price: tier.quarterly_price || 0,
    semiannual_price: tier.semiannual_price || 0,
    annual_price: tier.annual_price || 0,
  });

  const handleSave = () => {
    onSave(pricing);
  };

  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white capitalize">{tier.name}</h3>
        {!isEditing ? (
          <button onClick={onEdit} className="p-2 text-zinc-400 hover:text-purple-400 transition">
            <Edit size={18} />
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={handleSave} className="p-2 text-green-400 hover:text-green-300 transition">
              <Save size={18} />
            </button>
            <button onClick={onCancel} className="p-2 text-red-400 hover:text-red-300 transition">
              <X size={18} />
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {["monthly", "quarterly", "semiannual", "annual"].map((period) => (
          <div key={period} className="flex items-center justify-between">
            <span className="text-zinc-400 capitalize">{period}</span>
            {isEditing ? (
              <input
                type="number"
                step="0.01"
                value={pricing[`${period}_price`]}
                onChange={(e) => setPricing({ ...pricing, [`${period}_price`]: parseFloat(e.target.value) })}
                className="w-32 px-3 py-1 bg-zinc-800 border border-white/10 rounded text-white text-right"
              />
            ) : (
              <span className="text-white font-semibold">${tier[`${period}_price`]?.toFixed(2) || "0.00"}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Promos Tab
function PromosTab({ showPromoForm, setShowPromoForm, onPromoCreated }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPromos();
  }, []);

  const loadPromos = async () => {
    try {
      const res = await api.get("/api/promo-codes/list");
      setPromos(res.data.promo_codes || []);
    } catch (err) {
      toast.error("Failed to load promos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (promoId) => {
    const promo = promos.find(p => p.id === promoId);
    if (!confirm(`Delete promo code "${promo?.code}"?`)) return;
    try {
      await api.delete(`/api/promo-codes/${promoId}`);
      toast.success("Promo deleted");
      loadPromos();
    } catch (err) {
      toast.error("Failed to delete promo");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Promo Codes</h2>
        <button
          onClick={() => setShowPromoForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
          <Plus size={18} />
          Create Promo Code
        </button>
      </div>

      {showPromoForm && (
        <PromoForm
          onClose={() => setShowPromoForm(false)}
          onCreated={() => {
            loadPromos();
            setShowPromoForm(false);
            onPromoCreated();
          }}
        />
      )}

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto text-zinc-500" size={32} />
        </div>
      ) : promos.length === 0 ? (
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-12 text-center">
          <Ticket className="mx-auto text-zinc-600 mb-4" size={48} />
          <p className="text-zinc-500">No promo codes yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {promos.map((promo) => (
            <PromoCard key={promo.id} promo={promo} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

function PromoCard({ promo, onDelete }) {
  const copyCode = () => {
    navigator.clipboard.writeText(promo.code);
    toast.success("Code copied!");
  };

  const isExpired = promo.expires_at && new Date(promo.expires_at) < new Date();
  const isMaxed = promo.max_redemptions && promo.redemptions >= promo.max_redemptions;

  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="px-3 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 font-mono font-bold rounded">
          {promo.code}
        </span>
        <button onClick={copyCode} className="p-2 text-zinc-400 hover:text-white transition">
          <Copy size={16} />
        </button>
      </div>
      <div className="text-sm space-y-1">
        <div className="text-white font-semibold">
          {promo.discount_percent}% OFF
        </div>
        <div className="text-zinc-400">
          Used: {promo.redemptions || 0} {promo.max_redemptions ? `/ ${promo.max_redemptions}` : ''}
        </div>
        {promo.expires_at && (
          <div className={`text-xs ${isExpired ? 'text-red-400' : 'text-zinc-500'}`}>
            {isExpired ? '⚠️ Expired' : `Expires: ${new Date(promo.expires_at).toLocaleDateString()}`}
          </div>
        )}
        {isMaxed && <div className="text-xs text-yellow-400">⚠️ Max uses reached</div>}
        {promo.description && (
          <div className="text-xs text-zinc-500 mt-1">{promo.description}</div>
        )}
      </div>
      <button
        onClick={() => onDelete(promo.id)}
        className="mt-3 w-full px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm font-semibold rounded transition">
        Delete
      </button>
    </div>
  );
}

function PromoForm({ onClose, onCreated }) {
  const [formData, setFormData] = useState({
    code: "",
    discount_percent: 30,
    duration: "once",
    max_redemptions: 100,
    expires_at: "",
    description: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/promo-codes/create", {
        code: formData.code.toUpperCase(),
        discount_percent: formData.discount_percent,
        duration: formData.duration,
        max_redemptions: formData.max_redemptions || null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
        description: formData.description || null,
      });
      toast.success("Promo code created!");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create promo");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-white/10 rounded-xl max-w-md w-full">
        <div className="p-6 border-b border-white/5">
          <h3 className="text-xl font-bold text-white">Create Promo Code</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Code</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white uppercase"
              placeholder="SUMMER2025"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Discount %</label>
            <select
              value={formData.discount_percent}
              onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
            >
              <option value={30}>30% OFF</option>
              <option value={40}>40% OFF</option>
              <option value={50}>50% OFF</option>
              <option value={60}>60% OFF</option>
              <option value={70}>70% OFF</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Duration</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
            >
              <option value="once">Once (first payment)</option>
              <option value="forever">Forever</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Expires (optional)</label>
            <input
              type="date"
              value={formData.expires_at}
              onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Max Uses</label>
            <input
              type="number"
              value={formData.max_redemptions}
              onChange={(e) => setFormData({ ...formData, max_redemptions: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
              min="1"
              placeholder="100"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-semibold text-white mb-2">Description (optional)</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 bg-zinc-800 border border-white/10 rounded text-white"
              placeholder="Summer sale"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition">
              Create
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg transition">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Subscribers Tab
function SubscribersTab({ subscribers }) {
  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-800/50 border-b border-white/5">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Email</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Tier</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Status</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-300">Since</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center text-zinc-500">
                  No subscribers yet
                </td>
              </tr>
            ) : (
              subscribers.map((sub) => (
                <tr key={sub.subscription_id} className="border-b border-white/5 hover:bg-white/5 transition">
                  <td className="px-6 py-4 text-white">{sub.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      sub.tier === "vip" ? "bg-purple-500/20 text-purple-300" : "bg-blue-500/20 text-blue-300"
                    }`}>
                      {sub.tier.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      sub.status === "active" ? "bg-green-500/20 text-green-300" : "bg-zinc-700 text-zinc-400"
                    }`}>
                      {sub.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-zinc-400 text-sm">
                    {new Date(sub.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div style={{ color }}>{icon}</div>
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-sm text-zinc-400">{label}</div>
    </div>
  );
}

function getCategoryIcon(category) {
  const icons = {
    pricing_suggestions: "💰",
    promo_strategies: "🎟️",
    ppv_pricing: "📹",
    growth_opportunities: "🚀",
  };
  return icons[category] || "💡";
}
